// ✅ Billing Cycle Utilities - Handle date-to-date billing cycles for subscription plans

/**
 * Get the current billing cycle key for a user based on their subscription start date
 * Returns format: "YYYY-MM-DD" representing the start of current billing period
 *
 * Example: If user subscribed on Oct 15, 2025:
 * - Oct 15 - Nov 14: returns "2025-10-15"
 * - Nov 15 - Dec 14: returns "2025-11-15"
 * - Dec 15 - Jan 14: returns "2025-12-15"
 */
export const getCurrentBillingCycle = (userData) => {
  if (!userData) return null;

  // For free plan users or users without subscription start, use calendar month
  if (!userData.subscriptionStartDate || userData.planId === 'free') {
    return new Date().toISOString().slice(0, 7); // Calendar month: "2025-11"
  }

  const subscriptionStart = userData.subscriptionStartDate?.toDate
    ? userData.subscriptionStartDate.toDate()
    : new Date(userData.subscriptionStartDate);

  const dayOfMonth = subscriptionStart.getDate(); // e.g., 15
  const now = new Date();
  const currentDay = now.getDate();

  // Determine current billing cycle start
  let billingCycleStart;

  if (currentDay >= dayOfMonth) {
    // Current billing period (e.g., Nov 15 - Dec 14 if subscribed on Oct 15)
    billingCycleStart = new Date(now.getFullYear(), now.getMonth(), dayOfMonth);
  } else {
    // Previous month's billing period (e.g., Oct 15 - Nov 14 if today is Nov 10)
    billingCycleStart = new Date(now.getFullYear(), now.getMonth() - 1, dayOfMonth);
  }

  return billingCycleStart.toISOString().slice(0, 10); // "2025-10-15"
};

/**
 * Get the end date of the current billing cycle
 */
export const getBillingCycleEnd = (userData) => {
  if (!userData) return null;

  if (!userData.subscriptionStartDate || userData.planId === 'free') {
    // End of calendar month
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + 1, 0);
  }

  const subscriptionStart = userData.subscriptionStartDate?.toDate
    ? userData.subscriptionStartDate.toDate()
    : new Date(userData.subscriptionStartDate);

  const dayOfMonth = subscriptionStart.getDate();
  const now = new Date();
  const currentDay = now.getDate();

  let billingCycleEnd;

  if (currentDay >= dayOfMonth) {
    // Next month, day before subscription day
    billingCycleEnd = new Date(now.getFullYear(), now.getMonth() + 1, dayOfMonth - 1, 23, 59, 59);
  } else {
    // Current month, day before subscription day
    billingCycleEnd = new Date(now.getFullYear(), now.getMonth(), dayOfMonth - 1, 23, 59, 59);
  }

  return billingCycleEnd;
};

/**
 * Get days remaining in current billing cycle
 */
export const getDaysRemainingInCycle = (userData) => {
  const cycleEnd = getBillingCycleEnd(userData);
  if (!cycleEnd) return 0;

  const now = new Date();
  const daysRemaining = Math.ceil((cycleEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  return Math.max(0, daysRemaining);
};

/**
 * Check if user needs to be downgraded to free plan due to expired subscription
 */
export const checkSubscriptionExpiry = (userData) => {
  if (!userData || userData.planId === 'free') {
    return { needsDowngrade: false };
  }

  // If no subscription end date set, subscription is assumed active
  if (!userData.subscriptionEndDate && !userData.currentPeriodEnd) {
    return { needsDowngrade: false };
  }

  const endDate = userData.currentPeriodEnd?.toDate
    ? userData.currentPeriodEnd.toDate()
    : userData.subscriptionEndDate?.toDate
    ? userData.subscriptionEndDate.toDate()
    : new Date(userData.subscriptionEndDate || userData.currentPeriodEnd);

  const now = new Date();

  if (now > endDate) {
    return {
      needsDowngrade: true,
      expiredOn: endDate,
      daysExpired: Math.floor((now.getTime() - endDate.getTime()) / (1000 * 60 * 60 * 24))
    };
  }

  return { needsDowngrade: false, expiresOn: endDate };
};

/**
 * Format billing cycle for display
 */
export const formatBillingCycle = (userData) => {
  if (!userData) return 'Unknown';

  if (!userData.subscriptionStartDate || userData.planId === 'free') {
    const now = new Date();
    return now.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }

  const cycleStart = getCurrentBillingCycle(userData);
  const cycleEnd = getBillingCycleEnd(userData);

  if (!cycleStart || !cycleEnd) return 'Unknown';

  const start = new Date(cycleStart);
  return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${cycleEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
};
